/**
 * Dünner fetch-Wrapper. Keine externe HTTP-Library — wir nutzen die Browser-
 * fetch-API direkt und wrappen nur die Error-Propagation + JSON-Handling.
 */

// Base-URL aus Vite-ENV (während dev/e2e gesetzt). Leer in Prod — Caddy
// liefert Frontend + API unter derselben Origin aus.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export type ApiError = {
  status: number;
  code?: string;
  message: string;
  detail?: unknown;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });

  // 204/205 und leere Responses haben keinen Body — `res.json()` wuerde
  // sonst mit "Unexpected end of JSON input" crashen. Auch HEAD ist hier
  // abgedeckt. Wir unterscheiden:
  //   1. explizit kein Body (204/205, HEAD) → null
  //   2. Body vorhanden, JSON-Header → parse
  //   3. Body vorhanden, kein JSON → als Text
  const isEmpty =
    res.status === 204 || res.status === 205 || init.method === "HEAD";
  const contentType = res.headers.get("content-type") ?? "";
  let body: unknown = null;
  if (!isEmpty) {
    if (contentType.includes("application/json")) {
      // Fallback: manche Fehler-Antworten haben zwar den JSON-Header, aber
      // leeren Body (z. B. uvicorn 502). Dann text() lesen, leer → null.
      const raw = await res.text();
      body = raw ? JSON.parse(raw) : null;
    } else {
      body = await res.text();
    }
  }

  if (!res.ok) {
    const bodyObj = body as { detail?: unknown } | null;
    const detail = bodyObj && typeof bodyObj === "object" ? bodyObj.detail : undefined;
    const code = typeof detail === "object" && detail !== null
      ? (detail as { code?: string }).code
      : undefined;
    const message =
      typeof detail === "object" && detail !== null
        ? (detail as { message?: string }).message ?? res.statusText
        : typeof detail === "string"
          ? detail
          : res.statusText;
    const err: ApiError = { status: res.status, code, message, detail };
    throw err;
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// API-Typen
// ---------------------------------------------------------------------------

export type EventSummary = {
  shortcut: string;
  exists: boolean;
  name?: string | null;
  eventAt?: string | null;
  canceledAt?: string | null;
};

export type EventPublic = {
  shortcut: string;
  name: string;
  description: string | null;
  location: string | null;
  eventAt: string;
  timezone: string;
  deadline: string | null;
  creatorName: string | null;
  anonymous: boolean;
  canceledAt: string | null;
  cancelReason: string | null;
  expandable: boolean;
  participantsUsed: number;
  participantsMax: number | null;
  participants: Participant[];
  items: GuestItem[];
};

export type EventLimits = {
  participantsUsed: number;
  participantsMax: number | null;
  itemsUsed: number;
  itemsMax: number | null;
};

export type Participant = {
  id: number;
  name: string;
  participates: boolean;
  persons: number;
  createdAt: string;
};

export type ParticipantSelf = Participant & { userKey: string };

/**
 * Read-only-Item der öffentlichen Join-Seite. `claimed` bleibt auch dann
 * true, wenn `participantId` wegen `anonymous` verborgen ist. Ein volles
 * `Item` ist strukturell zuweisbar (`claimed` optional).
 */
export type GuestItem = {
  id: number;
  name: string;
  comment: string | null;
  position: number;
  forAllUsers: boolean;
  participantId: number | null;
  claimed?: boolean;
};

export type Item = {
  id: number;
  name: string;
  comment: string | null;
  position: number;
  forAllUsers: boolean;
  participantId: number | null;
  userComment: string | null;
  deletedAt: string | null;
  createdAt: string;
};

export type EventAdmin = {
  id: number;
  shortcut: string;
  adminKey: string;
  name: string;
  description: string | null;
  location: string | null;
  eventAt: string;
  originalEventAt: string;
  timezone: string;
  deadline: string | null;
  creatorName: string | null;
  creatorEmail: string | null;
  creatorEmailVerified: boolean;
  notifyOnChanges: boolean;
  notifySummary: boolean;
  lang: string;
  expandable: boolean;
  anonymous: boolean;
  canceledAt: string | null;
  cancelReason: string | null;
  tier: "free" | "pro";
  proActivatedAt: string | null;
  createdAt: string;
  participants: Participant[];
  items: Item[];
  limits: EventLimits;
};

export type EventUser = {
  shortcut: string;
  name: string;
  description: string | null;
  location: string | null;
  eventAt: string;
  timezone: string;
  deadline: string | null;
  creatorName: string | null;
  lang: string;
  expandable: boolean;
  anonymous: boolean;
  canceledAt: string | null;
  cancelReason: string | null;
  me: ParticipantSelf;
  participants: Participant[];
  items: Item[];
};
